/* eslint-disable no-nested-ternary */
// @mui
import { alpha, keyframes, useTheme, styled } from '@mui/material/styles';
import { Box, Card, Stack } from '@mui/material';
// hooks
import { useLocation } from 'react-router';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';
// locales
import { useLocales } from 'src/locales';
//
import AuthStatisticsLayout from '../_common/auth/auth-statistics-layout';

// ----------------------------------------------------------------------

type Props = {
  title?: string;
  image?: string;
  showLogo?: boolean;
  children: React.ReactNode;
};

// ----------------------------------------------------------------------

const pulseGlow = keyframes`
  0%   { background-size: 20% 20%, 20% 20%; }
  50%  { background-size: 60% 60%, 60% 60%; }
  100% { background-size: 20% 20%, 20% 20%; }
`;

const move = keyframes`
  0% {
    transform: translateX(-10%) translateY(0%);
  }
  50% {
    transform: translateX(10%) translateY(-5%);
  }
  100% {
    transform: translateX(-10%) translateY(0%);
  }
`;

// ----------------------------------------------------------------------

export default function AuthClassicLayout({ children, image, title, showLogo = true }: Props) {
  const { t } = useLocales();
  const { pathname } = useLocation();
  const theme = useTheme();

  const mdUp = useResponsive('up', 'md');

  const isLoginPage = pathname?.includes('login');

  // ----------------------------------------------------------------------

  const renderLogo = (
    <Stack
      sx={{
        width: 1,
        alignItems: 'center',
      }}
    >
      <Logo
        sx={{
          width: 60,
          mb: 3,
        }}
      />
    </Stack>
  );

  // ----------------------------------------------------------------------

  const renderContent = (
    <Stack
      sx={{
        width: 1,
        mx: 'auto',
        my: { xs: isLoginPage ? 8 : 'auto', lg: 'auto' },
        maxWidth: 480,
        px: { xs: 2, md: 8 },
        borderRight: {
          xs: 'none',
          lg: isLoginPage ? `1px solid ${theme.palette.divider}` : 'none',
        },
        zIndex: 5,
      }}
    >
      <Card
        sx={{
          p: 3.5,
          borderRadius: 3,
          // backdropFilter: 'blur(22px)',
          background: alpha(theme.palette.background.paper, 0.72),
          // border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          // boxShadow: `0 0 80px ${alpha(theme.palette.primary.main, 0.14)}`,
          overflow: { md: 'unset' },
          position: 'relative',
          backdropFilter: 'blur(18px)',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.darker, 0.1)
              : alpha(theme.palette.common.white, 0.75),

          border:
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid rgba(15,23,42,0.08)',

          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 40px 120px rgba(0,0,0,0.6)'
              : '0 30px 80px rgba(15,23,42,0.12)',
        }}
      >
        {showLogo && renderLogo}

        {children}
      </Card>
    </Stack>
  );

  // ----------------------------------------------------------------------

  return (
    <Stack
      component="main"
      direction={{ xs: 'column', lg: 'row' }}
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background:
          theme.palette.mode === 'light'
            ? `
  radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.12)}, transparent 35%),
  radial-gradient(circle at bottom left, ${alpha(theme.palette.info.main, 0.1)}, transparent 35%),
  linear-gradient(
    145deg,
    #f7f9fc 0%,
    #eef3f9 45%,
    #fdfdff 100%
  )
`
            : `
          radial-gradient(circle at top right, ${alpha(
            theme.palette.primary.main,
            0.24
          )}, transparent 30%),
          radial-gradient(circle at bottom left, ${alpha(
            theme.palette.info.main,
            0.18
          )}, transparent 28%),
          linear-gradient(
            145deg,
            #050816 0%,
            #09111f 45%,
            #040b16 100%
          )
        `,
        [theme.breakpoints.down('md')]: {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage: `
    radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(
              theme.palette.primary.main,
              0.1
            )} 40%, transparent 20%),
    radial-gradient(circle at 0% 100%, ${alpha(theme.palette.info.main, 0.2)} 0%, ${alpha(
              theme.palette.info.main,
              0.08
            )} 40%, transparent 20%)
  `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '20% 20%, 20% 20%',
            filter: 'blur(50px)',
            animation: `${pulseGlow} 10s ease-in-out infinite`,
          },
        },
      }}
    >
      {/* Animated AI Background */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          zIndex: 0,
        }}
      >
        {mdUp
          ? [...Array(18)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 280,
                  height: 280,
                  borderRadius: '50%',
                  position: 'absolute',
                  background: alpha(
                    index % 2 === 0 ? theme.palette.primary.main : theme.palette.info.main,
                    0.08
                  ),
                  filter: 'blur(120px)',
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animation: `${move} ${10 + index}s ease-in-out infinite`,
                }}
              />
            ))
          : null}
      </Box>

      {renderContent}

      {/* {upMd && renderSection} */}

      {isLoginPage && <AuthStatisticsLayout />}
    </Stack>
  );
}
